/** The AI suggestion of a new qualification: polling rules and which fields are still "AI". */

import { MAX_ACTIVE_TASKS, createFormTask } from './qualificationTasks';
import { isConditionsValue } from './qualificationRequirements';
import { normalizeTaskLabel } from 'components/SlideOver/dataSlideOver/data/slideOverAddMission/missionTemplate/templateFormData';

export const AI_SUGGEST_POLL_INTERVAL_MS = 2000;
// The api turns a suggestion pending for more than 4 minutes into AI_TIMEOUT;
// the screen gives up at the same point rather than polling forever.
export const AI_SUGGEST_CLIENT_TIMEOUT_MS = 4 * 60 * 1000;

export const AI_TITLE_MIN_LENGTH = 3;
export const AI_TITLE_MAX_LENGTH = 120;

// The last three come from the risk and requirements tabs: conditions with
// the classification, tools and licences with the writing.
export const AI_FIELDS = [
  'name',
  'description',
  'rome',
  'pcs',
  'sector',
  'conditions',
  'tools',
  'licences',
];

/**
 * Whether a suggestion actually carries a field. The requirement fields may be
 * missing from an answer; a field the AI did not propose is not traced as AI.
 */
export const isFieldProposed = (field, suggestion) => {
  if (field === 'conditions') return isConditionsValue(suggestion?.conditions);
  if (field === 'tools' || field === 'licences') {
    return Array.isArray(suggestion?.[field]);
  }
  return true;
};

export const AI_STATUS = {
  PENDING: 'pending',
  CLASSIFIED: 'classified',
  DONE: 'done',
  FAILED: 'failed',
};

export const GENERATION_STEP_STATE = {
  DONE: 'done',
  IN_PROGRESS: 'inProgress',
  WAITING: 'waiting',
};

// In the order the api runs them: the ROME candidates are searched first, the
// classification then picks codes and sector; description, tasks and
// requirements are written once the classification is known.
export const AI_GENERATION_STEPS = [
  {
    id: 'codes',
    startsAt: [AI_STATUS.PENDING, AI_STATUS.CLASSIFIED, AI_STATUS.DONE],
    doneAt: [AI_STATUS.CLASSIFIED, AI_STATUS.DONE],
  },
  {
    id: 'sector',
    startsAt: [AI_STATUS.PENDING, AI_STATUS.CLASSIFIED, AI_STATUS.DONE],
    doneAt: [AI_STATUS.CLASSIFIED, AI_STATUS.DONE],
  },
  {
    id: 'description',
    startsAt: [AI_STATUS.CLASSIFIED, AI_STATUS.DONE],
    doneAt: [AI_STATUS.DONE],
  },
  {
    id: 'tasks',
    startsAt: [AI_STATUS.CLASSIFIED, AI_STATUS.DONE],
    doneAt: [AI_STATUS.DONE],
  },
  // Risk factors come with the classification, but tools and licences with the
  // writing: the step is only done once both are known.
  {
    id: 'requirements',
    startsAt: [AI_STATUS.CLASSIFIED, AI_STATUS.DONE],
    doneAt: [AI_STATUS.DONE],
  },
];

export const isGenerationStepDone = (step, status) =>
  step.doneAt.includes(status);

export const getGenerationStepState = (step, status) => {
  if (isGenerationStepDone(step, status)) return GENERATION_STEP_STATE.DONE;
  if (step.startsAt.includes(status)) return GENERATION_STEP_STATE.IN_PROGRESS;
  return GENERATION_STEP_STATE.WAITING;
};

export const countDoneGenerationSteps = (status) =>
  AI_GENERATION_STEPS.filter((step) => isGenerationStepDone(step, status))
    .length;

export const isValidAiTitle = (title) => {
  const length = String(title ?? '').trim().length;
  return length >= AI_TITLE_MIN_LENGTH && length <= AI_TITLE_MAX_LENGTH;
};

export const noAiFields = () =>
  AI_FIELDS.reduce((fields, field) => ({ ...fields, [field]: false }), {});

/** Fields left exactly as the AI proposed them, as the api expects in `aiFields`. */
export const listAiFields = (aiFields) =>
  AI_FIELDS.filter((field) => Boolean(aiFields?.[field]));

const hasUntouchedAiTask = (tasks) =>
  tasks.some((task) => task.source === 'ai');

/**
 * `regenerate.keep`: what the admin made their own. Tasks are kept once no
 * untouched AI task is left, since there is nothing to replace any more.
 */
export const buildRegenerateKeep = (aiFields, tasks) => {
  const keep = AI_FIELDS.filter((field) => !aiFields?.[field]);
  if (!hasUntouchedAiTask(tasks)) keep.push('tasks');
  return keep;
};

/** AI task labels as slide-over tasks, cleaned and without duplicates. */
export const toSuggestedFormTasks = (suggestedTasks) => {
  const seen = new Set();
  return (Array.isArray(suggestedTasks) ? suggestedTasks : []).reduce(
    (formTasks, task) => {
      const formTask = createFormTask(task?.label ?? '');
      const key = normalizeTaskLabel(formTask.label);
      if (!formTask.label || seen.has(key)) return formTasks;
      seen.add(key);
      formTasks.push({ ...formTask, source: 'ai' });
      return formTasks;
    },
    []
  );
};

/**
 * Untouched AI tasks are replaced by the new proposal, placed first as the AI
 * ones were at the start; the admin's tasks stay, and win on a duplicate label.
 * The 50-task cap trims the new AI tasks, never the admin's.
 */
export const mergeSuggestedTasks = (currentTasks, suggestedTasks) => {
  const adminTasks = currentTasks.filter((task) => task.source !== 'ai');
  const adminKeys = new Set(
    adminTasks.map((task) => normalizeTaskLabel(task.label))
  );
  const newAiTasks = toSuggestedFormTasks(suggestedTasks)
    .filter((task) => !adminKeys.has(normalizeTaskLabel(task.label)))
    .slice(0, Math.max(0, MAX_ACTIVE_TASKS - adminTasks.length));
  return [...newAiTasks, ...adminTasks];
};

/** The form values a `done` suggestion proposes, field by field. */
export const toSuggestedValues = (suggestion) => ({
  name: suggestion?.name ?? '',
  description: suggestion?.descriptionHtml ?? '',
  rome: suggestion?.rome?.code ?? '',
  pcs: suggestion?.pcs?.code ?? '',
  sector: suggestion?.sector?.type ?? '',
});

/**
 * draft-js reports focus and selection as changes, and rewrites the HTML it
 * was given: compare the visible text, not the markup.
 */
export const toComparableText = (html) =>
  String(html ?? '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#x([0-9a-f]+);/gi, (_, hex) =>
      String.fromCodePoint(parseInt(hex, 16))
    )
    .replace(/&#(\d+);/g, (_, decimal) =>
      String.fromCodePoint(parseInt(decimal, 10))
    )
    .replace(/\s+/g, ' ')
    .trim();

/** ROME codes are a letter and 4 digits; the field keeps letters and digits, upper-cased. */
export const normalizeRomeInput = (value) =>
  String(value ?? '')
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
    .slice(0, 5);

/** Same name for the admin: case and edge spaces aside. */
export const isSameQualificationName = (left, right) =>
  String(left ?? '')
    .trim()
    .toLocaleLowerCase('fr') ===
  String(right ?? '')
    .trim()
    .toLocaleLowerCase('fr');

/** The AI's other name for the job, offered only while it differs from the name typed. */
export const getAlternativeNameOffer = (alternativeName, currentName) => {
  const offer = String(alternativeName ?? '').trim();
  if (!offer || isSameQualificationName(offer, currentName)) return '';
  return offer;
};

/** Sentences of a description, for the generation step summary. */
export const countSentences = (html) =>
  toComparableText(html)
    .split(/[.!?…]+(?:\s|$)/)
    .filter((sentence) => sentence.trim()).length;

/** Risk factors the AI checked in `conditions.penibilite`. */
export const countRiskFactors = (conditions) =>
  Object.values(conditions?.penibilite ?? {}).filter((value) => value === true)
    .length;
